import { z } from 'zod';

// Helper to validate number inputs (InputNumber returns number | null)
// These are optional fields that can be null/undefined
const numberField = z.union([
  z.number().min(0, 'Must be a positive number'),
  z.null(),
  z.undefined()
]).optional();

const requiredNumberField = z.union([
  z.number().min(0, 'Required and must be a positive number'),
  z.null(),
  z.undefined()
]).refine((val) => val !== null && val !== undefined, {
  message: 'This field is required'
});

const _percentageField = z.union([
  z.number().min(0, 'Must be between 0 and 100').max(100, 'Must be between 0 and 100'),
  z.null(),
  z.undefined()
]).optional();

const requiredPercentageField = z.union([
  z.number().min(0, 'Must be between 0 and 100').max(100, 'Must be between 0 and 100'),
  z.null(),
  z.undefined()
]).refine((val) => val !== null && val !== undefined, {
  message: 'This field is required and must be between 0 and 100'
});

// Custom validation for odd/even check on suggested bid price
// Only invalid when odd whole number is inputted (like 33.00, 21.00)
// Valid if it has decimal part (like 33.22, 21.21)
const createSuggestedBidPriceValidation = () => z
  .union([z.number().min(0, 'Must be a positive number'), z.null(), z.undefined()])
  .refine((val) => val !== null && val !== undefined, {
    message: 'Suggested BID Price is required'
  })
  .refine((val) => {
    // If null or undefined, already caught by required check above
    if (val === null || val === undefined) return true;
    
    // Check if it's a whole number (no decimal part)
    const isWholeNumber = val % 1 === 0;
    
    // Only invalid if it's a whole number AND the integer part is odd
    if (isWholeNumber) {
      const integerPart = Math.floor(val);
      // Validation fails only if it's an odd whole number
      return integerPart % 2 !== 1;
    }
    
    // If it has decimal part, it's always valid
    return true;
  }, {
    message: 'BID_PRICE_VA_VALIDATION_FAILED', // Special marker for custom formatting
  });

// Custom validation for GSMS Profit
// Allows negative values (cost > price scenario) for all analysis types
const createGsmProfitValidation = (_analysisType: string) => {
  // Allow negative values for all analysis types (cost can exceed price)
  return z.union([
    z.number().min(-100, 'Must be between -100 and 100').max(100, 'Must be between -100 and 100'),
    z.null(),
    z.undefined()
  ]).optional();
};

// Validation schema factory for Direct Product
export const createDirectProductSchema = (analysisType: string = 'New Launch') => z.object({
  // Current Market State
  vaAnnualUsage: requiredNumberField,
  dodAnnualUsage: requiredNumberField,

  // COGS Buildup
  gsmsCostFromMfg: numberField,
  gsmsPackSize: z.union([
    z.number().int('Must be a whole number').min(1, 'Must be at least 1').max(10000, 'Must be at most 10000'),
    z.null(),
    z.undefined()
  ]).optional(),
  gsmsDesiccantCost: numberField,
  blendedWhlsFee: numberField,

  // Pricing Calculator
  vaTmsPercent: requiredPercentageField,
  dodTmsPercent: requiredPercentageField,
  suggestedBidPrice: createSuggestedBidPriceValidation(),
  gsmsProfit: createGsmProfitValidation(analysisType),

  // TMOP
  tmopPercent: requiredPercentageField,

  // FSS Tracking
  proposedNewRatio: numberField,
  awardedFssRatio: numberField,
  newTcPrice: numberField,
  maxPriceWoutIff: numberField,
  maxPriceWithIff: numberField,
}).partial();

// Validation schema factory for Indirect Product
export const createIndirectProductSchema = (analysisType: string = 'New Launch') => z.object({
  // Current Market State
  vaAnnualUsage: requiredNumberField,
  dodAnnualUsage: requiredNumberField,

  // COGS Buildup (Indirect only has 4 fields)
  gsmsCostFromMfg: numberField,

  // Pricing Calculator
  vaTmsPercent: requiredPercentageField,
  dodTmsPercent: requiredPercentageField,
  suggestedBidPrice: createSuggestedBidPriceValidation(),
  gsmsProfit: createGsmProfitValidation(analysisType),

  // TMOP
  tmopPercent: requiredPercentageField,

  // FSS Tracking
  proposedNewRatio: numberField,
  awardedFssRatio: numberField,
  newTcPrice: numberField,
  maxPriceWoutIff: numberField,
  maxPriceWithIff: numberField,
}).partial();

// Backward compatibility - export default schemas
export const directProductSchema = createDirectProductSchema();
export const indirectProductSchema = createIndirectProductSchema();

export type DirectProductFormData = z.infer<typeof directProductSchema>;
export type IndirectProductFormData = z.infer<typeof indirectProductSchema>;

